import React, { FC, useMemo, useState } from 'react';
import { DesktopDatePicker, DesktopDatePickerProps, LocalizationProvider } from '@mui/x-date-pickers';
import { validateInput } from '../../utils/validators/Validators';

import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { identifiers } from '../../utils/constants/identifiers';
import { FormValidators } from '../Form';
import { TextField } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import 'dayjs/locale/de';
import strings from 'common/Translation/Translate';
import 'dayjs/locale/pt';
import 'dayjs/locale/en';
import { useSelector } from 'react-redux';
import { defaultLanguage } from 'utils/redux/reducer/authentication-slice';
import { DateInputProps } from 'components/DateInput/DateInput';

dayjs.extend(customParseFormat);

const toMonthInputString = (next: unknown): string => {
  if (next == null || next === '') return '';
  if (dayjs.isDayjs(next)) {
    return next.isValid() ? next.format(identifiers.month_input) : '';
  }
  if (next instanceof Date) {
    const parsed = dayjs(next);
    return parsed.isValid() ? parsed.format(identifiers.month_input) : '';
  }
  if (typeof next === 'string') {
    const asMonth = dayjs(next, identifiers.month_input, true);
    if (asMonth.isValid()) return asMonth.format(identifiers.month_input);
    const parsed = dayjs(next);
    return parsed.isValid() ? parsed.format(identifiers.month_input) : '';
  }
  return '';
};

const toMonthPickerDayjs = (value: unknown): Dayjs | null => {
  if (value == null || value === '') return null;
  if (dayjs.isDayjs(value)) return value.isValid() ? value : null;
  if (typeof value === 'string') {
    const asMonth = dayjs(value, identifiers.month_input, true);
    if (asMonth.isValid()) return asMonth;
    const loose = dayjs(value);
    return loose.isValid() ? loose : null;
  }
  if (value instanceof Date) {
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed : null;
  }
  return null;
};

const MonthPicker: FC<DateInputProps & DesktopDatePickerProps<any, any>> = ({
  hasError,
  validators,
  textChange,
  field,
  value,
  placeholder,
  fieldError,
  disabled,
  disableFuture,
  minDate,
  maxDate,
  label,
  typeValue,
  size,
  ...rest
}) => {
  const [error, setError] = useState<{
    error?: boolean;
    message?: string;
  } | null>({ error: hasError, message: fieldError as string });

  const selectedlangLocaleCode = useSelector(defaultLanguage).value.split('_')[0];
  const pickerValue = useMemo(() => toMonthPickerDayjs(value), [value]);

  const onError = (pickerError: any, next: any) => {
    const fomattedMinDate = minDate
      ? minDate.format(identifiers.date_format)
      : dayjs('1900-01-01').format(identifiers.date_format);
    const fomattedMaxDate = maxDate
      ? maxDate.format(identifiers.date_format)
      : dayjs('2099-12-12').format(identifiers.date_format);
    let message = '';
    if (pickerError === 'minDate') {
      message = strings.formatString(strings.max_date_error, fomattedMinDate) as string;
    } else if (pickerError === 'maxDate') {
      message = strings.formatString(strings.max_date_error, fomattedMaxDate) as string;
    } else if (pickerError != null) {
      message = strings.date_format_error;
    }
    const errorMsg = { error: pickerError != null, message };
    setError(errorMsg);

    const formatted = toMonthInputString(next);
    if (formatted) {
      textChange(formatted, field as string, errorMsg);
      return;
    }
    if (pickerError == null) {
      textChange('', field as string, errorMsg);
      return;
    }
    textChange(toMonthInputString(value), field as string, errorMsg);
  };

  const customMsg = () => (error && hasError && fieldError ? strings.requiredText : '');

  const handleChange = (next: Dayjs | null) => {
    if (!next) return;
    const formatted = toMonthInputString(next);
    if (!formatted) return;
    const inputError = validateInput(validators as FormValidators[], formatted);
    textChange(formatted, field as string, inputError);
    if (inputError.error) {
      setError(inputError);
    } else {
      setError(null);
    }
  };

  return (
    <LocalizationProvider adapterLocale={selectedlangLocaleCode} dateAdapter={AdapterDayjs}>
      <DesktopDatePicker
        {...rest}
        data-testid="datepicker"
        views={['month', 'year']}
        label={label}
        minDate={minDate || null}
        maxDate={maxDate || null}
        inputFormat={identifiers.month_input}
        value={pickerValue}
        onChange={(newValue: Dayjs | null) => handleChange(newValue)}
        disabled={disabled}
        className={`input-field ${fieldError}`}
        OpenPickerButtonProps={{ color: 'primary' }}
        disableFuture={disableFuture}
        onError={onError}
        PopperProps={{
          sx: { zIndex: 1400 },
        }}
        renderInput={params => (
          <TextField
            {...params}
            size={size}
            helperText={error && error.error ? error.message : customMsg()}
            error={error?.error || (hasError && fieldError) ? true : false}
          />
        )}
      />
    </LocalizationProvider>
  );
};

export default MonthPicker;
